import { useAppDispatch, useAppSelector } from './hooks';
import { updateAICredits } from '../redux/organization/organization.slice';
import { minimalToast } from '../utils/toast';
import { createNotification } from '../redux/notifications/notification.slice';
import { useSseStream } from './useSseStream';

/**
 * The organisation these streams are opened for. Both endpoints are org-scoped
 * server side: /sse/ai-credits only ever sends the token's own organisation's
 * balance, and /sse/notifications needs an organisation to mint a token for
 * even though it filters on the token's userId.
 *
 * Read from redux rather than taken as an argument because App.tsx mounts both
 * hooks with no arguments at all - which is also why credit events never used
 * to arrive: the broadcast has always filtered by organizationId and this
 * client never sent one.
 */
const useSseOrganizationId = (): string | undefined =>
    useAppSelector((state) => state.organization.selectedOrganization?._id);

export function useAICreditSSE() {
    const dispatch = useAppDispatch();
    const organizationId = useSseOrganizationId();

    useSseStream({
        path: '/sse/ai-credits',
        organizationId,
        listeners: {
            ai_credit_deduction: (event: MessageEvent) => {
                try {
                    const data = JSON.parse(event.data);
                    dispatch(
                        updateAICredits({
                            organizationId: data.organizationId,
                            aiCredits: data.aiCredits,
                        })
                    );
                } catch (err) {
                    console.error('Error parsing AI credit deduction data:', err);
                }
            },
        },
    });
}

export function useNotificationSSE() {
    const dispatch = useAppDispatch();
    const organizationId = useSseOrganizationId();

    useSseStream({
        path: '/sse/notifications',
        organizationId,
        listeners: {
            notification: (event: MessageEvent) => {
                try {
                    const notification = JSON.parse(event.data);
                    dispatch(createNotification(notification));
                    minimalToast({
                        dispatch: dispatch,
                        _id: notification._id,
                        title: notification.title,
                        message: notification.message,
                        type: notification.severity,
                        category: notification.category,
                        priority: notification.priority,
                        link: notification.link,
                        data: notification.data,
                        expiresAt: notification.expiresAt
                            ? new Date(notification.expiresAt)
                            : undefined,
                    });
                } catch (err) {
                    console.error('Error parsing notification SSE data:', err);
                }
            },
        },
    });
}
